import { formatCalendarDate } from '@app/shared/helpers/calendar-date';
import {
  Component,
  OnInit,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '@env/environment';
import { BrowserMultiFormatReader, BrowserQRCodeReader, DecodeHintType } from '@zxing/library';
import {
  MolstFormViewResponse,
  SecureMolstResponse,
} from '@app/modules/molst-form/interface/molst-form.interface';
import { SnackNotifier } from '@app/shared/notifier/snack-notifier';
import { DeviceTokenService } from '@app/shared/services/device-token.service';
import {
  isMinimalVerifyResponse,
  MinimalVerifyResponse,
} from '@app/shared/interfaces/tiered-verify';

@Component({
  selector: 'app-public-molst-lookup',
  templateUrl: './public-molst-lookup.component.html',
  styleUrls: ['./public-molst-lookup.component.sass'],
})
export class PublicMolstLookupComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('video') videoElement!: ElementRef<HTMLVideoElement>;

  public molstForm: MolstFormViewResponse | null = null;
  // MINIMAL tier (unregistered device): emergency summary instead of the form.
  public minimal: MinimalVerifyResponse | null = null;
  public isLoading = false;
  public showScanner = true;
  public scannedResult: string | null = null;
  public error: string | null = null;
  public isDragging = false;
  // Upload is the default; the camera only starts when the user opts in.
  public cameraOn = false;

  /**
   * Where a signed-in user can go back to. This page is PUBLIC (emergency
   * break-glass), so an anonymous responder gets no navigation chrome at all —
   * but a clinician who reached it from the app's QR action was previously
   * stranded with only the browser's back button. Read once from the persisted
   * session; never blocks the page.
   */
  public returnLink: string | null = null;

  // --- Blockchain integrity / audit state (shown to the clinician) ---
  public integrityChecked = false;         // true when the secure verify endpoint was used
  public integrityResult: 'pass' | 'fail' | 'local_only' | 'unknown' | null = null;
  public isCurrentVersion = true;
  public anchoredTxId: string | null = null;
  public blockchainStatus: string | null = null; // legacy-path anchoring status

  private codeReader: BrowserMultiFormatReader;
  // Dedicated QR-only reader for decoding uploaded/dropped images — more reliable
  // on static images than the multi-format camera reader.
  private imageReader: BrowserQRCodeReader;
  private destroy$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    private router: Router,
    private snack: SnackNotifier,
    private deviceTokens: DeviceTokenService
  ) {
    this.codeReader = new BrowserMultiFormatReader();
    const hints = new Map();
    hints.set(DecodeHintType.TRY_HARDER, true);
    this.imageReader = new BrowserQRCodeReader();
    this.imageReader.hints = hints;
  }

  ngOnInit(): void {
    this.returnLink = this.resolveReturnLink();
    // Preferred: secure, blockchain-verified lookup by opaque token.
    const qrToken = this.route.snapshot.paramMap.get('qrToken');
    if (qrToken) {
      this.loadByToken(qrToken);
      this.showScanner = false;
      return;
    }
    // Legacy: direct lookup by integer id (already-printed wristbands).
    const molstId = this.route.snapshot.paramMap.get('molstId');
    if (molstId) {
      this.loadMolstForm(molstId);
      this.showScanner = false;
    }
  }

  /** Same persisted session the user store reads ('softhread-user'). */
  private resolveReturnLink(): string | null {
    try {
      const raw = window.localStorage.getItem('softhread-user');
      if (!raw) {
        return null;
      }
      const user = JSON.parse(raw);
      if (!user || !user.token) {
        return null;
      }
      return user.role === 'doctor' ? '/office/dashboard/doctor' : '/office/dashboard';
    } catch {
      return null;
    }
  }

  ngAfterViewInit(): void {
    // Do NOT auto-start the camera — upload/drag-drop is the default. The camera
    // only turns on if the user explicitly clicks "Use camera".
  }

  /** Opt-in camera scanning. */
  public enableCamera(): void {
    this.error = null;
    this.cameraOn = true;
    // Let the *ngIf render the <video> element before we attach the stream.
    setTimeout(() => this.startCamera(), 0);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopCamera();
  }

  startCamera(): void {
    if (!this.videoElement) return;

    this.codeReader
      .decodeFromVideoDevice(
        null,
        this.videoElement.nativeElement,
        (result, err) => {
          if (result) {
            this.routeDecoded(result.getText());
          }
          if (err && !(err.name === 'NotFoundException')) {
            console.error('Error scanning:', err);
            this.error = 'Error scanning QR code. Please try again.';
          }
        }
      )
      .catch((err) => {
        console.error('Error starting camera:', err);
        this.error = 'Unable to access camera. Please check permissions.';
      });
  }

  stopCamera(): void {
    this.codeReader.reset();
  }

  // --- Upload / drag-and-drop a QR image (no camera needed) ---

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = true;
  }

  public onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
    const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
    if (file) {
      this.decodeImageFile(file);
    }
  }

  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (file) {
      this.decodeImageFile(file);
    }
    input.value = '';
  }

  /** Decode a QR code from an uploaded/dropped image, then route to its verify page. */
  private async decodeImageFile(file: File): Promise<void> {
    if (!file.type.startsWith('image/')) {
      this.error = 'Please drop an image file (PNG, JPG, or a screenshot).';
      return;
    }
    this.error = null;
    const url = URL.createObjectURL(file);
    try {
      const result = await this.imageReader.decodeFromImageUrl(url);
      this.routeDecoded(result.getText());
    } catch (err) {
      this.error = 'Could not read a QR code from that image. Try a clearer, closer picture.';
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  /**
   * Route a decoded QR payload. VA advance-directive QRs point at the public
   * directive-verify page; everything else is handled as a MOLST code.
   */
  private routeDecoded(text: string): void {
    const va = text.match(
      /directive-verify\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i,
    );
    if (va) {
      this.stopCamera();
      this.router.navigate(['/directive-verify', va[1]]);
      return;
    }
    this.handleScannedResult(text);
  }

  handleScannedResult(result: string): void {
    // Preferred: a secure token URL, e.g. .../public-molst-lookup/verify/<uuid>
    // or the backend verify path .../molst/verify/<uuid>. Also accept a bare uuid.
    const tokenMatch = result.match(
      /(?:verify\/)([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i
    );
    const bareUuid = result
      .trim()
      .match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
    if (tokenMatch || bareUuid) {
      const token = tokenMatch ? tokenMatch[1] : bareUuid[0];
      this.loadByToken(token);
      this.showScanner = false;
      this.stopCamera();
      return;
    }

    try {
      // Try to parse the scanned result as JSON
      const parsed = JSON.parse(result);
      if (parsed.molstId) {
        this.loadMolstForm(parsed.molstId);
        this.showScanner = false;
        this.stopCamera();
        return;
      }
    } catch (e) {
      // If not JSON, try to extract a molst id from a URL-like string (legacy)
      const urlMatch = result.match(/(?:molst-form|public-molst-lookup)\/(\d+)/);
      if (urlMatch) {
        this.loadMolstForm(urlMatch[1]);
        this.showScanner = false;
        this.stopCamera();
        return;
      }
    }

    // If we can't parse the result, show error
    this.error =
      'Invalid QR code format. Please scan a valid MOLST form QR code.';
  }

  /**
   * Secure path: verify the form against the blockchain and record the access.
   * Returns the form PLUS the integrity verdict, which we surface to the clinician.
   */
  loadByToken(qrToken: string): void {
    this.isLoading = true;
    this.error = null;

    // Tiered disclosure: a REGISTERED device sends its token and gets the full
    // form; without it the backend returns the minimal emergency summary.
    const deviceToken = this.deviceTokens.getToken();
    const options = deviceToken
      ? { headers: new HttpHeaders({ 'X-Device-Token': deviceToken }) }
      : {};

    this.http
      .get<SecureMolstResponse | MinimalVerifyResponse>(
        `${environment.apiUrl}/molst/verify/${qrToken}`,
        options
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (isMinimalVerifyResponse(response)) {
            this.minimal = response;
            this.isLoading = false;
            return;
          }
          this.molstForm = response.form;
          this.integrityChecked = true;
          this.integrityResult = response.integrityResult;
          this.isCurrentVersion = response.isCurrentVersion;
          this.anchoredTxId = response.anchoredTxId;
          this.blockchainStatus = response.form.blockchainStatus || null;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error verifying MOLST form:', error);
          this.error = 'No MOLST form matches this code.';
          this.isLoading = false;
        },
      });
  }

  loadMolstForm(molstId: string): void {
    this.isLoading = true;
    this.error = null;

    // Tiered disclosure, same as the token path: a registered device sends its
    // token and receives the full form; everyone else gets the minimal
    // emergency summary from the legacy id URL.
    const deviceToken = this.deviceTokens.getToken();
    const options = deviceToken
      ? { headers: new HttpHeaders({ 'X-Device-Token': deviceToken }) }
      : {};

    this.http
      .get<MolstFormViewResponse | MinimalVerifyResponse>(
        `${environment.apiUrl}/molst-forms/${molstId}`,
        options
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (isMinimalVerifyResponse(response)) {
            this.minimal = response;
            this.integrityChecked = true;
            this.integrityResult = response.integrityResult;
            this.isLoading = false;
            return;
          }
          this.molstForm = response;
          // Legacy path: no live verify, but the access is still audited on-chain
          // and the form carries its anchoring status/tx id.
          this.integrityChecked = false;
          this.anchoredTxId = response.blockchainTxId || null;
          this.blockchainStatus = response.blockchainStatus || null;
          this.isCurrentVersion = response.status === 'active';
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading MOLST form:', error);
          this.error = 'MOLST form not found or access denied.';
          this.isLoading = false;
        },
      });
  }

  resetScanner(): void {
    this.showScanner = true;
    this.molstForm = null;
    this.minimal = null;
    this.error = null;
    this.scannedResult = null;
    // Return to the upload view; the camera stays off until opted into again.
    this.cameraOn = false;
    this.stopCamera();
  }

  // getCriticalSummary method removed - no longer needed with detailed form display

  // Calendar dates (DOB!) must never shift a day with the viewer's timezone —
  // the shared helper parses date-only strings as local calendar dates.
  formatDate(dateString: string): string {
    return formatCalendarDate(dateString);
  }

  getCprOptionLabel(option: string): string {
    switch (option) {
      case 'attemptCpr':
        return 'Attempt CPR';
      case 'noCprOptionA':
        return 'Do Not Attempt CPR (DNR) - Option A';
      case 'noCprOptionB':
        return 'Do Not Attempt CPR (DNR) - Option B';
      default:
        return option;
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'active':
        return 'status-active';
      case 'draft':
        return 'status-draft';
      case 'superseded':
        return 'status-superseded';
      default:
        return 'status-default';
    }
  }
}
